// ProjectContext.jsx
// Project properties that are shared between many components.

// Copyright HS Analysis GmbH, 2019
// Author: Viktor Eberhardt, Sebastian Murgul, Jakob Burkhardt, Valentin Haas

// Framework imports
import React from "react";
import PropTypes from "prop-types";

// External imports

// Other imports
import RBush from "rbush";

// HSA imports
import { createRoisFromAnno } from "../utils/ROI";
import {
  getParentIndexLayer,
  updateStructures,
} from "../utils/StructuresUtils";
import { getUnusedColor } from "../../common/utils/RandomColorGenerator";
import Backend from "../../common/utils/Backend";
import Structure from "../../common/components/Structure";

const ProjectContext = React.createContext();
export { ProjectContext };

export const withProject = (Component) => {
  const WrappedComponent = (props) => (
    <ProjectContext.Consumer>
      {(context) => <Component {...props} projectContext={context} />}
    </ProjectContext.Consumer>
  );

  WrappedComponent.displayName = `withProject(${
    Component.displayName || Component.name || "Component"
  })`;

  return WrappedComponent;
};

// this is the main component, it has to be added at the root of the app.
// all components that use withProject(...) will have access to it via this.props.Project...
class ProjectProvider extends React.Component {
  constructor(props) {
    super(props);

    this._isMounted = false;
    this.state = {
      heatMapOpacity: 50,
      wasSavedByUser: false,
      structures: [],
      roiLayers: {},
      commentLayers: {},
      selectedLayer: 0,
      ome: null,
      activeTab: 0,
      changingFile: false,
      user: "",
      isLoadingAnnotations: false,
      galleryImageSize: 300,
      userPermissions: {
        canImportModels: true,
        canExportModels: true,
      },
      groupPermissions: {
        canAnnotate: true,
        canTrainModel: true,
        canRunJob: true,
        canComment: true,
        canEditStructures: true,
        canAccessAITab: true,
        canEditModel: true,
      },
      annotationsReduced: false,
      totalRoiCount: -1,
      shortCutButtonIdList: [],
      aiModel2ProjectModuleMappings: [],
      allAiModels: [],
      projectStructures: [],
      opacity: 1.2,
      previewScaleFactor: 1,
      layoutStateObject: {
        fileNavButtons: false,
        imageInfo: true,
        macroImage: true,
        miniMap: true,
        objectIdx: false,
        resultTable: false,
        scaleBar: true,
        slideLabel: true,
        timeBar: true,
        zoomBar: true,
        zStackBar: true,
      },
    };
    const storedLayoutStateObject = localStorage.getItem(
      "hsaProjectLayoutStateObject"
    );
    if (storedLayoutStateObject) {
      this.state.layoutStateObject = JSON.parse(storedLayoutStateObject);
    }
    this.usersData = {};
    Backend.getModelMetadata("hsa_models", false)
      .then((aiModels) => {
        this.setMountedState({ allAiModels: aiModels.newModels });
      })
      .catch((error) => {
        console.error("Error loading model metadata: ", error);
      });
  }

  setMountedState = (stateObject, callback) => {
    if (this._isMounted) {
      this.setState(stateObject, callback);
    }
  };

  componentDidMount() {
    this._isMounted = true;
  }

  componentWillUnmount() {
    this._isMounted = false;
  }

  toggleLayoutStateItem = (itemName) => {
    const { layoutStateObject } = this.state;
    const newLayoutStateObject = {
      ...layoutStateObject,
      [itemName]: !layoutStateObject[itemName],
    };
    this.setMountedState({ layoutStateObject: newLayoutStateObject });
    localStorage.setItem(
      "hsaProjectLayoutStateObject",
      JSON.stringify(newLayoutStateObject)
    );
  };

  // init ai models
  initAIData = () => {
    Backend.readAIModel2ProjectModuleMapping().then((mappingData) => {
      this.setMountedState({ aiModel2ProjectModuleMappings: mappingData });
      if (this.state.wasSavedByUser) return;

      // Project was not saved yet
      const projectObject = { ...this.state.project };
      const structures = projectObject.viewerConfig.project.structures;
      const projectType = this.state.project.type;
      const filteredMappingData = mappingData.filter(
        (item) => item.projectType === projectType
      );

      for (const mappingItem of filteredMappingData) {
        for (const structureMapping of mappingItem.structureMappings) {
          const newId = structureMapping.newId;
          const structureIdx = structures.findIndex((s) => s.id === newId);
          const structure = structures[structureIdx];
          if (!structure) continue;

          const toolIdx = structure.tools.findIndex(
            (t) => t.name === "iam_ai_inference"
          );
          const tool = structure.tools[toolIdx];
          for (const toolParam of tool.toolParams) {
            switch (toolParam.name) {
              case "ai_model_name":
                if (mappingItem.isActive) {
                  toolParam.ui.default = mappingItem.aiModel;
                  toolParam.ui.value = mappingItem.aiModel;
                }
                break;
              case "area_min":
                toolParam.ui.default =
                  mappingItem.inferenceParameters.minObjectSize;
                break;
              case "area_max":
                toolParam.ui.default =
                  mappingItem.inferenceParameters.maxObjectSize;
                break;
              case "intensity_range":
                toolParam.ui.default = [
                  mappingItem.inferenceParameters.minIntensity,
                  mappingItem.inferenceParameters.maxIntensity,
                ];
                break;
              case "confience_threshold":
                toolParam.ui.default =
                  mappingItem.inferenceParameters.confidence;
                break;
              case "depending_structure":
                toolParam.ui.default =
                  mappingItem.inferenceParameters.dependingStructure;
                break;
            }
          }
        }
      }
      this.setMountedState({ project: projectObject });
    });
  };

  // init ai model to projectType mapping
  initModel2ProjectTypeMapping = () => {
    Backend.readAIModel2ProjectModuleMapping().then((response) => {
      this.setMountedState({ aiModel2ProjectModuleMappings: response });
    });
  };

  // set project from viewer, when project opens
  initProject = (project) => {
    this.setMountedState({ project }, () => {
      const projectStructures = project.viewerConfig.project.structures.map(
        (structure) => {
          let stuct = { ...structure };
          if (stuct.parentId === 0) {
            stuct.parentId = null;
          }
          return Structure.fromObject(stuct);
        }
      );
      if (projectStructures) {
        this.setMountedState({ projectStructures });
      }
    });
    if (project.viewerConfig.project.toolsInProject.AICockpit) {
      this.initAIData();
    }
    // this.initSocket(project.id); // TODO: fix by changing flask-sock to socketio
  };

  initForeignHistory = () => {
    if (this.socket) {
      this.socket.send(
        JSON.stringify({
          state: "init",
        })
      );
    }
  };

  componentDidUpdate = () => {
    if (this.persistentStorage && this.state.project) {
      const structuresStateList = this.state.structures.map((structure) => {
        return {
          isUnfolded: structure.isUnfolded,
          showSubtypes: structure.showSubtypes,
          visible: structure.visible,
          color: structure.color,
        };
      });
      this.persistentStorage.saveProjectTypeValue(
        this.state.project.type,
        "structuresStateList",
        structuresStateList
      );
    }
  };

  updateProjectStructures = (structures) => {
    const projectStructures = structures.map((structure) => {
      return Structure.fromObject(structure);
    });
    this.setMountedState({ structures, projectStructures });
  };

  //remove users, that didn't send updates for 10 seconds
  cleanUsersData = () => {
    const timeNow = performance.now();
    for (const key of Object.keys(this.usersData)) {
      if (timeNow - this.usersData[key].updateTime > 10000) {
        console.debug(
          "Old user deleted from usersData:",
          this.usersData[key].user
        );
        delete this.usersData[key];
      }
    }
  };

  receiveSocketMessage = (data) => {
    if (data.state) {
      if (data.state === "pos") {
        data.updateTime = performance.now();
        this.usersData[data.uuid] = data;
        this.cleanUsersData();
      } else if (data.state === "syncStructures") {
        this.syncStructures(data.structures);
      } else {
        window.projectHistory.receive(data);
      }
    }
  };

  syncStructures = (structures) => {
    const { roiLayers } = this.state;
    const fileRoiLayers = roiLayers[this.state.fileId];
    const newRoiLayers = [];
    for (const structure of structures) {
      const tempRoiLayer = fileRoiLayers.find(
        (roiLayer) => roiLayer.id === structure.id
      );
      if (tempRoiLayer) {
        newRoiLayers.push(tempRoiLayer);
      } else {
        newRoiLayers.push({
          id: structure.id,
          layer: {
            regionRois: [],
            inverted: false,
          },
          tree: new RBush(),
        });
      }
    }

    roiLayers[this.state.fileId] = newRoiLayers;
    this.updateProjectStructures(structures);
    this.setMountedState({
      roiLayers: roiLayers,
    });
  };

  // set web socket for communication between current viewer/user and server
  initSocket = (projectId) => {
    this.socket = Backend.getAnnotationSocket(projectId);
    window.projectHistory.socket = this.socket;
    this.socket.onmessage = (ev) => {
      const data = JSON.parse(ev.data);
      this.receiveSocketMessage(data);
    };

    Backend.getCurrentUser((user) => {
      this.setMountedState({ user: user.fullName });
      let lastSendObject = null;

      const intervalId = setInterval(() => {
        if (
          this.socket.readyState === 1 &&
          typeof window.getMousePositionInImage === "function"
        ) {
          const p = window.getMousePositionInImage();
          let objectToSend = {
            state: "pos",
            projectId: projectId,
            user: user.fullName,
            x: parseInt(p.x, 10),
            y: parseInt(p.y, 10),
          };
          // send new position, if it has changed
          if (
            lastSendObject === null ||
            lastSendObject.x === objectToSend.x ||
            lastSendObject.y === objectToSend.y
          ) {
            this.socket.send(JSON.stringify(objectToSend));
          }
        } else {
          console.log("socket not ready, readyState:", this.socket.readyState);
          if (this.socket.readyState === 3) {
            console.log("socket broke, stopping interval!");
            clearInterval(intervalId);
          }
        }
      }, 100);
    });
  };

  /**
   * Initializes the Project provider and passes a viewer component
   *
   * @param {object} viewer - Viewer Component
   */
  init = (viewer) => {
    this.viewer = viewer;
  };

  /**
   * Updates the roiLayers with the annotations for projectModel
   * @param {object} project - projectModel with annotations
   */
  updateProject = (project) => {
    const { roiLayers, fileId, structures } = this.state;
    for (const file of project.files) {
      for (const structureIdx of file.annotations.map((a) =>
        // Caution!: Legacy datastructure: id is Index sometimes!
        // See also https://hsa.atlassian.net/wiki/spaces/HSAKIT/pages/16116235173917/Annotations+Data+Structure#Legacy
        a.id >= 0
          ? // Assigned Id
            a.id
          : // Save to currently selected structure
            project.structures.findIndex((s) => s.id === project.selStructureId)
      )) {
        const anno = file.annotations[structureIdx];
        const structureId = structures[structureIdx].id;
        const newRegionRois = createRoisFromAnno(anno, structureId);
        if (newRegionRois.length > 0 || fileId === file.id) {
          roiLayers[file.id][structureIdx].layer.regionRois = newRegionRois;
          roiLayers[file.id][structureIdx].tree.load(
            newRegionRois.map((item) => item.treeItem)
          );
        }
      }
    }
    this.setState({ roiLayers: roiLayers });
  };

  setChangingFile = (a) => {
    this.setMountedState({ changingFile: a });
  };

  setActiveTab = (tab) => {
    this.setMountedState({ activeTab: tab });
    this.activeTab = tab;
    this.forceUpdate();
  };

  getActiveTab = () => {
    return this.state.activeTab;
  };

  /**
   * Returns Roi Region Object
   *
   * @param {int} structureId - id of the structure
   * @param {int} fileId - id of the file, if undefined the currently opened file will be used
   */
  getRegionById = (structureId, fileId) => {
    if (!fileId) fileId = this.state.fileId;
    return this.state.roiLayers[fileId].find((c) => c.id === structureId);
  };

  getNumberOfChilds = (parentStructure) => {
    const { structures } = this.state;

    return structures.filter(
      (element) => element.parentId === parentStructure.id
    ).length;
  };

  importStructures = (structures) => {
    const { roiLayers } = this.state;

    for (let structure of structures) {
      if (structure.tools.length === 0) {
        if (structure.parentId > 0) {
          let parentStructure = structures.find(
            (item) => item.id === structure.parentId
          );
          if (parentStructure) {
            structure.tools = parentStructure.tools;
          }
        }
      }
      for (let layerId in roiLayers) {
        const roiLayer = roiLayers[layerId].find((c) => c.id === structure.id);
        if (!roiLayer) {
          roiLayers[layerId].push({
            id: structure.id,
            layer: {
              regionRois: [],
              inverted: false,
            },
            tree: new RBush(),
          });
        }
      }
    }

    this.sendStructures(structures);
    this.updateProjectStructures(structures);
    this.setMountedState({ roiLayers });
  };

  /**
   * Adds a new Dynamic Structure to the Project Model
   *
   * @param {string} name - Display name of the new structure
   */
  addStructure = (name, grid = false) => {
    const { structures, roiLayers, viewerConfig } = this.state;

    // findHighest id number
    const newLayerId = structures.reduce((a, c) => Math.max(a, c.id), 0) + 1;

    let newStructure = {
      id: newLayerId,
      dynamic: true,
      label: name,
      color: grid ? "#00000000" : getUnusedColor(structures, false),
      visible: true,
      allToolNames: structures[0].allToolNames,
      tools: viewerConfig.project.dynamicStructure.tools,
      classFrequencies: {
        class_0: 0,
        class_1: 0,
        class_2: 0,
        class_3: 0,
        class_4: 0,
        class_5: 0,
        class_6: 0,
      },
      avgClassFrequnecy: 0,
      isSubtype: false,
      isUnfolded: true,
      classificationSubtype: false,
      parentId: null,
      showSubtypes: false,
      subtypeLevel: 0,
      defaultSelected: false,
    };
    newStructure = this.viewer.alterStructure(newStructure);

    for (let layerId in roiLayers) {
      let newRoilayer = {
        id: newLayerId,
        layer: {
          regionRois: [],
          inverted: false,
        },
        tree: new RBush(),
      };
      roiLayers[layerId].push(newRoilayer);
    }

    structures.push(newStructure);

    this.sendStructures(structures);
    this.updateProjectStructures(structures);
    this.setMountedState({ roiLayers });
  };

  // update structure for other users without saving yet
  // eslint-disable-next-line no-unused-vars
  sendStructures = (structures) => {
    // console.log("sending structures wip:", structures);
    // const { project } = this.state;
    // const toSend = {
    //   state: "syncStructures",
    //   projectId: project.id,
    //   structures: structures,
    // };
    // this.socket.send(JSON.stringify(toSend));
  };

  addSubStructure = (parent, name, fromHistoModule) => {
    const { structures, roiLayers, selectedLayer } = this.state;

    // findHighest id number
    const newLayerId = structures.reduce((a, c) => Math.max(a, c.id), 0) + 1;
    const newColor = getUnusedColor(structures, false);

    const subStructure = {
      id: newLayerId,
      dynamic: true,
      parentId: parent.id,
      label: name ? name : parent.label,
      color: newColor,
      visible: true,
      allToolNames: parent.allToolNames,
      tools: parent.tools, // what tools should be used?
      classFrequencies: {
        class_0: 0,
        class_1: 0,
        class_2: 0,
        class_3: 0,
        class_4: 0,
        class_5: 0,
        class_6: 0,
      },
      avgClassFrequnecy: 0,
      showSubtypes: false,
      isSubtype: true,
      parentColor: parent.color,
      subtypeColor: newColor,
      isUnfolded: true,
      subtypeLevel: parent.subtypeLevel + 1,
      hasChild: false,
      classificationSubtype: false,
      defaultSelected: false,
    };
    // add substructure as last child (for now just in histo modules / for other modules check if this works too)
    let numberChilds = 0;
    if (fromHistoModule) {
      numberChilds = this.getNumberOfChilds(structures[selectedLayer]);
    }

    structures.splice(selectedLayer + 1 + numberChilds, 0, subStructure);

    for (let key in roiLayers) {
      const roiIndex = roiLayers[key].findIndex(
        (c) => c.id === structures[selectedLayer].id
      );
      roiLayers[key].splice(roiIndex + 1, 0, {
        id: newLayerId,
        layer: {
          regionRois: [],
          inverted: false,
        },
        tree: new RBush(),
      });
    }
    parent.addSubtypeText = "";
    parent.hasChild = true;

    this.sendStructures(structures);
    this.updateProjectStructures(structures);
    this.setMountedState({ roiLayers });
  };

  addSubType = (parent, name, fromHistoModule) => {
    const { structures, roiLayers, selectedLayer } = this.state;

    // findHighest id number
    const newLayerId = structures.reduce((a, c) => Math.max(a, c.id), 0) + 1;

    const subStructure = {
      id: newLayerId,
      dynamic: true,
      parentId: parent.id,
      label: name ? name : parent.label,
      color: getUnusedColor(structures, false),
      visible: true,
      allToolNames: parent.allToolNames,
      tools: parent.tools, // what tools should be used?
      classFrequencies: {
        class_0: 0,
        class_1: 0,
        class_2: 0,
        class_3: 0,
        class_4: 0,
        class_5: 0,
        class_6: 0,
      },
      avgClassFrequnecy: 0,
      showSubtypes: false,
      isSubtype: true,
      isUnfolded: fromHistoModule ? false : true,
      subtypeLevel: parent.subtypeLevel + 1,
      hasChild: false,
      classificationSubtype: true,
      defaultSelected: false,
    };
    // add substructure as last child (for now just in histo modules / for other modules check if this works too)
    let numberChilds = 0;
    if (fromHistoModule) {
      numberChilds = this.getNumberOfChilds(structures[selectedLayer]);
      // for point counting module only 11 subtypes are possible because of template
      if (numberChilds >= 10) {
        window.showWarningSnackbar("Reached maximum amount of subtypes.");
        return;
      }
    }

    structures.splice(selectedLayer + 1 + numberChilds, 0, subStructure);

    for (let key in roiLayers) {
      const roiIndex = roiLayers[key].findIndex(
        (c) => c.id === structures[selectedLayer].id
      );

      roiLayers[key].splice(roiIndex + 1 + numberChilds, 0, {
        id: newLayerId,
        layer: {
          regionRois: [],
          inverted: false,
        },
        tree: new RBush(),
      });
    }
    parent.addSubtypeText = "";
    parent.hasChild = true;
    this.sendStructures(structures);
    this.updateProjectStructures(structures);
    this.setMountedState({ roiLayers });
  };

  findChilds = (subType) => {
    const { structures } = this.state;

    return structures.filter(
      (element) =>
        element.subtypeLevel === subType.subtypeLevel + 1 &&
        element.parentId === subType.id
    );
  };

  /**
   * Removes a structure and corresponding regions from project model.
   *
   * @param {object} structure - Structure to remove.
   * @returns {Int} The id of the next layer to be selected.
   */
  deleteStructure = (structure) => {
    const { selectedLayer } = this.state;
    let { structures, roiLayers } = this.state;

    // Recursively delete all children.
    if (structure.hasChild) {
      let childs = this.findChilds(structure);
      for (let i = 0; i < childs.length; i++) {
        this.deleteStructure(childs[i]);
      }
    }

    let toplevelParentId = null;
    // In case of subtypes, find the next (sub-)structure
    if (structure.classificationSubtype) {
      toplevelParentId =
        structures[getParentIndexLayer(structure, structures)].id;
    }
    // Otherwise, get the direct parent
    else {
      toplevelParentId = structure.parentId;
    }

    // Delete from structure
    const structureIndex = structures.findIndex((c) => c.id === structure.id);
    structures.splice(structureIndex, 1);

    // Delete from ROIs.
    for (let key in roiLayers) {
      const roiIndex = roiLayers[key].findIndex((c) => c.id === structure.id);
      roiLayers[key].splice(roiIndex, 1);
    }

    // In case of subtype, delete ROIs from containing layer.
    if (structure.isSubtype) {
      for (let key in roiLayers) {
        // Get structure where ROIs are stored
        let roiIdx = roiLayers[key].findIndex(
          (struct) => struct.id === toplevelParentId
        );

        // Only keep ROIs not from the deleted subtype
        let subtypesRemoved = roiLayers[key][roiIdx].tree
          .all()
          .filter((treeItem) => {
            return (
              treeItem.roi.isSubtype &&
              treeItem.roi.subtypeName !== structure.label
            );
          });

        // Overwrite previous ROIs
        roiLayers[key][roiIdx].tree.data.children = subtypesRemoved;
      }
    }
    // Update children status of parent.
    let parentStruct = structures.find(
      (struct) => struct.id === structure.parentId
    );
    // Only update if there is something to update
    if (parentStruct) {
      structures = updateStructures(parentStruct, structures);
    }

    // if last layer gets deleted make selectedLayer 0
    let newSelectedLayer =
      selectedLayer >= structures.length ? 0 : selectedLayer;

    this.sendStructures(structures);
    this.updateProjectStructures(structures);
    this.setMountedState({
      roiLayers,
      selectedLayer: newSelectedLayer,
    });

    return newSelectedLayer;
  };

  /**
   * Duplicates a structure and corresponding parameters from project model.
   *
   * @param {object} structure - Structure to remove
   */
  duplicateStructure = (structure) => {
    const { structures, roiLayers } = this.state;

    // findHighest id number
    const newLayerId =
      structures.reduce((a, c) => (a ? Math.max(a, c.id) : c.id), 0) + 1;

    // clone structure
    const pureLabel = structure.label.split(" - copy")[0];
    const copiesCount = structures.reduce(
      (a, c) => (c.label.startsWith(pureLabel + " - copy") ? a + 1 : a),
      0
    );
    let newStructure = Object.assign(JSON.parse(JSON.stringify(structure)), {
      id: newLayerId,
      label:
        copiesCount > 0
          ? pureLabel + " - copy(" + (copiesCount + 1) + ")"
          : pureLabel + " - copy",
    });

    structures.push(newStructure);

    for (let layerId in roiLayers) {
      let newRoilayer = {
        id: newLayerId,
        layer: {
          regionRois: [],
          inverted: false,
        },
        tree: new RBush(),
      };
      roiLayers[layerId].push(newRoilayer);
    }

    this.duplicateChildren(structure, newStructure);

    let newSelectedLayer = structures.findIndex((c) => c.id === newLayerId);

    this.sendStructures(structures);
    this.updateProjectStructures(structures);
    this.setMountedState({
      roiLayers,
      selectedLayer: newSelectedLayer,
    });

    return newSelectedLayer;
  };

  findChildren = (structure) => {
    const { structures } = this.state;

    return structures.filter(
      (element) =>
        element.subtypeLevel === structure.subtypeLevel + 1 &&
        element.parentId === structure.id
    );
  };

  duplicateChildren = (oldParent, newParent) => {
    const { structures, roiLayers } = this.state;

    for (let structure of this.findChildren(oldParent)) {
      // findHighest id number
      const newId =
        structures.reduce((a, c) => (a ? Math.max(a, c.id) : c.id)) + 1;

      // clone structure
      let newStructure = Object.assign(JSON.parse(JSON.stringify(structure)), {
        id: newId,
        parentId: newParent.id,
      });

      structures.push(newStructure);
      for (let layerId in roiLayers) {
        let newRoilayer = {
          id: newId,
          layer: {
            regionRois: [],
            inverted: false,
          },
          tree: new RBush(),
        };
        roiLayers[layerId].push(newRoilayer);
      }

      this.duplicateChildren(structure, newStructure);
    }
  };

  /**
   * Checks if a structure can be moved in a direction.
   *
   * @param {Object} selectedStructure - Structure to move
   * @param {int} direction - 1 is up -1 is down
   * @returns {bool} Move in this direction possible
   */
  canMoveStructure = (selectedStructure, direction) => {
    const { structures } = this.state;
    if (!selectedStructure.dynamic) return false;

    let structuresSameLevel = {
      structure: [],
      index: [],
    };

    // get structures in same level of same parent
    structuresSameLevel.structure = structures.filter(
      (element) =>
        element.parentId === selectedStructure.parentId &&
        element.subtypeLevel === selectedStructure.subtypeLevel
    );

    // if parent is selected (e.g. Base Roi)
    if (!selectedStructure.isSubtype) {
      structuresSameLevel.structure = structures.filter(
        (element) => element.subtypeLevel === selectedStructure.subtypeLevel
      );
    }
    // get indices in all structures
    structuresSameLevel.structure.forEach((element) =>
      structuresSameLevel.index.push(
        structures.findIndex((structure) => structure === element)
      )
    );

    // index of selected layer of structures in same level
    let index = structuresSameLevel.structure.findIndex(
      (structure) => structure === selectedStructure
    );

    if (direction > 0) {
      // if first object --> move up not possible.
      // Also, moving above fixed, non-dynamic structures is forbidden.
      if (index === 0 || !structuresSameLevel.structure[index - 1].dynamic) {
        return false;
      } else {
        return true;
      }
    } else {
      // if last object --> move down not possible
      if (index === structuresSameLevel.structure.length - 1) {
        return false;
      } else {
        return true;
      }
    }
  };

  /**
   * Moves a structure up or down in structure list
   *
   * @param {Object} selectedStructure - Structure to move
   * @param {int} direction - 1 is up -1 is down
   */
  moveStructure = (selectedStructure, direction) => {
    const { structures, roiLayers } = this.state;

    let structuresSameLevel = {
      structure: [],
      index: [],
    };

    // get structures in same level of same parent
    structuresSameLevel.structure = structures.filter(
      (element) =>
        element.parentId === selectedStructure.parentId &&
        element.subtypeLevel === selectedStructure.subtypeLevel
    );

    // if parent is selected (e.g. Base Roi)
    if (!selectedStructure.isSubtype) {
      structuresSameLevel.structure = structures.filter(
        (element) => element.subtypeLevel === selectedStructure.subtypeLevel
      );
    }
    // get indices in all structures
    structuresSameLevel.structure.forEach((element) =>
      structuresSameLevel.index.push(
        structures.findIndex((structure) => structure === element)
      )
    );

    // index of selected layer of structures in same level
    let index = structuresSameLevel.structure.findIndex(
      (structure) => structure === selectedStructure
    );

    if (direction > 0) {
      // if first object --> move up not possible.
      // Also, moving above fixed, non-dynamic structures is forbidden.
      if (index === 0 || !structuresSameLevel.structure[index - 1].dynamic) {
        window.showWarningSnackbar("Moving structure up not possible!");
        return;
      }
      // swap two neighbor structures with subtypes
      // get all elements of selected structure
      let strt = structuresSameLevel.index[index];
      let ed = structuresSameLevel.index[index + 1];
      // if structure to move up is last structure
      if (!ed) {
        // get index first structure in same level
        let idxFirstSubtype = structures.findIndex(
          (structure) => structure === structuresSameLevel.structure[0]
        );
        for (let i = idxFirstSubtype; i < structures.length; i++) {
          if (structures[i].subtypeLevel < selectedStructure.subtypeLevel) {
            ed = i;
            break;
          }
        }
      }
      // Get all children of the selected structure.
      let allElements_1 = structures.slice(strt, ed);

      // delete elements to swap from structures
      structures.splice(structuresSameLevel.index[index], allElements_1.length);

      // put elements to right index in structures
      let start = structures.findIndex(
        (structure) => structure === structuresSameLevel.structure[index - 1]
      );
      for (let i = allElements_1.length - 1; i >= 0; i--) {
        structures.splice(start, 0, allElements_1[i]);
      }
    } else {
      // if last object --> move down not possible
      if (index === structuresSameLevel.structure.length - 1) {
        window.showWarningSnackbar("Moving structure down not possible!");
        return;
      }

      // swap two neighbor structures with subtypes
      // get all elements of selected structure
      let strt = structuresSameLevel.index[index];
      let ed = structuresSameLevel.index[index + 1];
      let allElements_1 = structures.slice(strt, ed);

      // delete elements of first structure to swap
      structures.splice(structuresSameLevel.index[index], allElements_1.length);

      // put elements to right index in structures
      let start = structures.findIndex(
        (structure) => structure === structuresSameLevel.structure[index + 2]
      );

      // if structure to swap is last subtype
      let lastStructure = false;
      if (start === -1) {
        // get index of structure to swap
        let idxSwapStructure = structures.findIndex(
          (structure) => structure === structuresSameLevel.structure[index + 1]
        );
        // if it is the last structure of all structures
        // search next parent structure
        let otherParent = false;
        for (let i = idxSwapStructure; i < structures.length; i++) {
          if (structures[i].subtypeLevel < selectedStructure.subtypeLevel) {
            otherParent = true;
          }
        }
        if (!otherParent) {
          lastStructure = true;
        } else {
          // search next parent structure
          for (let i = idxSwapStructure; i < structures.length; i++) {
            if (structures[i].subtypeLevel < selectedStructure.subtypeLevel) {
              start = i;
              break;
            }
          }
        }
      }

      if (!lastStructure) {
        // put all elements to index after structure to swap
        for (let i = allElements_1.length - 1; i >= 0; i--) {
          structures.splice(start, 0, allElements_1[i]);
        }
      } else {
        // if last structure push elements to the end
        for (let i = 0; i < allElements_1.length; i++) {
          structures.push(allElements_1[i]);
        }
      }
    }

    // Match roiLayer structure to new structure structure.
    // Sorts the roilayers for each scene in project by having their ID-order
    // match the order set by the structures.
    for (let sceneGUID in roiLayers) {
      roiLayers[sceneGUID].sort((roi_layer_a, roi_layer_b) => {
        // Location of the first roi-Layer Id in strcutures
        let index_of_roi_layer_a_in_structures = structures.findIndex(
          (element) => {
            return element.id === roi_layer_a.id;
          }
        );

        // Location of the second roi-Layer Id in strcutures
        let index_of_roi_layer_b_in_structures = structures.findIndex(
          (element) => {
            return element.id === roi_layer_b.id;
          }
        );

        // Check if the first index comes before the second.
        // Else, swap per sort algorithm.
        return (
          index_of_roi_layer_a_in_structures -
          index_of_roi_layer_b_in_structures
        );
      });
    }

    let newSelectedLayer = structures.findIndex((c) => c === selectedStructure);

    this.sendStructures(structures);
    this.updateProjectStructures(structures);
    this.setMountedState({ selectedLayer: newSelectedLayer });
    return newSelectedLayer;
  };

  getProjectStringInfos = () => {
    const project = this.state.project;
    const file = project.files.find((file) => file.id === this.state.fileId);
    const fileName = file.fileName.split(".").slice(0, -1).join(".");
    let today = new Date();
    let dd = String(today.getDate()).padStart(2, "0");
    let mm = String(today.getMonth() + 1).padStart(2, "0"); //January is 0!
    let yyyy = today.getFullYear();
    today = yyyy + "-" + mm + "-" + dd;

    // projekt_methode_filename_SceneNr_001.png
    return {
      name: project.name,
      type: project.type,
      fileName: fileName,
      date: today,
    };
  };

  setStateNow = (newState) => {
    Object.assign(this.state, newState);
    this.forceUpdate();
  };

  getUserData = () => {
    return this.usersData;
  };

  setPersistentStorage = (persistentStorage) => {
    this.persistentStorage = persistentStorage;
  };

  render() {
    return (
      <ProjectContext.Provider
        value={{
          viewer: this.viewer,
          persistentStorage: this.persistentStorage,
          getUsersData: this.getUserData,
          setPersistentStorage: this.setPersistentStorage,
          init: this.init,
          initForeignHistory: this.initForeignHistory,
          initProject: this.initProject,
          setState: (e, callback) => this.setMountedState(e, callback),
          forceUpdate: () => this.forceUpdate(),
          getRegionById: this.getRegionById,
          setChangingFile: this.setChangingFile,
          setActiveTab: this.setActiveTab,
          getActiveTab: this.getActiveTab,
          /* Handle Structures */
          importStructures: this.importStructures,
          addStructure: this.addStructure,
          addSubStructure: this.addSubStructure,
          addSubType: this.addSubType,
          sendStructures: this.sendStructures,
          deleteStructure: this.deleteStructure,
          moveStructure: this.moveStructure,
          canMoveStructure: this.canMoveStructure,
          duplicateStructure: this.duplicateStructure,
          setStateNow: this.setStateNow,
          getProjectStringInfos: this.getProjectStringInfos,
          updateProject: this.updateProject,
          toggleLayoutStateItem: this.toggleLayoutStateItem,
          getFileRoiLayers: this.getFileRoiLayers,
          /* State */
          ...this.state,
          fileRoiLayers:
            this.state.roiLayers && this.state.roiLayers[this.state.fileId],
        }}
      >
        {this.props.children}
      </ProjectContext.Provider>
    );
  }
}

ProjectProvider.propTypes = {
  children: PropTypes.element.isRequired,
};

export default ProjectProvider;
